import { useNavigate } from "react-router-dom";
import { Boton } from "../../components/Boton";
import { Icono } from "../../components/Icono";
import { ESTILO_SEVERIDAD, useDesvanecido, type Severidad } from "./ComunesDashboard";

interface Alerta {
  tipo: string;
  etiqueta: string;
  cantidad: number;
  severidad: Severidad;
}

export type NivelEstado = "Estable" | "Atención" | "Riesgo";

const ESTILO_ESTADO: Record<NivelEstado, string> = {
  Estable: "bg-exito/10 text-exito",
  "Atención": "bg-advertencia/10 text-advertencia",
  Riesgo: "bg-error/10 text-error",
};

// Semáforo de la Vista General — componente NUEVO (spec: "Franja de
// Estado"), sin equivalente reutilizable en el resto del Dashboard: ninguna
// pantalla existente combina semáforo + alertas cortas + un único botón de
// salida en una sola franja horizontal.
//
// El nivel se calcula ACÁ pero sin inventar ninguna regla de negocio: sólo
// lee `severidad` ('info'|'advertencia'|'error'), un campo que
// lib/centro-operaciones.ts YA calcula por cada alerta (agregar()). Umbral
// simple y determinista, documentado:
//   - Riesgo    → al menos una alerta con severidad 'error'.
//   - Atención  → ninguna 'error', pero hay al menos una alerta (sea
//                 'advertencia' o 'info').
//   - Estable   → cero alertas.
export function calcularNivelEstado(alertas: Alerta[]): NivelEstado {
  if (alertas.some((a) => a.severidad === "error")) return "Riesgo";
  if (alertas.length > 0) return "Atención";
  return "Estable";
}

// Máximo 3 alertas visibles, priorizando severidad ('error' primero) —
// mismo orden de importancia que ya usa el semáforo de arriba, nunca un
// criterio nuevo. Texto corto (etiqueta + cantidad), nunca párrafos, nunca
// botones de acción por alerta — el único botón de la franja lleva al
// Centro de Operaciones, donde sí se puede actuar.
const RANGO_SEVERIDAD: Record<Severidad, number> = { error: 0, advertencia: 1, info: 2 };

function alertasPrioritarias(alertas: Alerta[]): Alerta[] {
  return [...alertas].sort((a, b) => RANGO_SEVERIDAD[a.severidad] - RANGO_SEVERIDAD[b.severidad]).slice(0, 3);
}

export function FranjaEstado({ alertas }: { alertas: Alerta[] }) {
  const visible = useDesvanecido();
  const navegar = useNavigate();
  const nivel = calcularNivelEstado(alertas);
  const top3 = alertasPrioritarias(alertas);

  return (
    <div
      className={`mb-3 flex flex-wrap items-center gap-3 rounded-2xl border border-borde bg-superficie px-5 py-3 shadow-md transition-opacity duration-500 ${
        visible ? "opacity-100" : "opacity-0"
      }`}
    >
      <span
        className={`flex flex-shrink-0 items-center gap-2 rounded-full px-3 py-1 text-sm font-semibold ${ESTILO_ESTADO[nivel]}`}
      >
        <span className="h-2 w-2 rounded-full bg-current" />
        {nivel}
      </span>

      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
        {top3.length === 0 ? (
          <span className="text-sm text-texto-secundario">Sin alertas pendientes. Todo al día.</span>
        ) : (
          top3.map((a) => (
            <span
              key={a.tipo}
              className={`inline-flex max-w-full items-center gap-1.5 truncate rounded-lg border-l-4 px-2.5 py-1 text-xs font-medium ${ESTILO_SEVERIDAD[a.severidad]}`}
              title={a.etiqueta}
            >
              <Icono nombre="alerta" className="h-3.5 w-3.5 flex-shrink-0" />
              <span className="truncate">{a.etiqueta}</span>
              <span className="flex-shrink-0 font-semibold">{a.cantidad}</span>
            </span>
          ))
        )}
      </div>

      <Boton variante="primario" tamano="sm" className="flex-shrink-0" onClick={() => navegar("/centro-operaciones")}>
        Ver seguimiento operativo
      </Boton>
    </div>
  );
}
